/**
 * DisclosureReviewCapture — a VIEW-ONLY disclosure is presented and the signer
 * acknowledges receipt. No signature, no initials: this records that the
 * document was delivered and reviewed (Bill 2026-09-30 — "the disclosures that
 * are not signed are not being presented to the signer in the signing session").
 *
 * History: 2026-07-02 dropped view-only disclosures from the ceremony because
 * a capture row per disclosure doubled its length and put signatures on
 * documents that require none; 2026-07-13 (Item 31) Bill asked how they were
 * delivered or attested; the archive-at-completion answer never SHOWED them.
 * This primitive is the presentation. The host renders the document itself
 * (`children` — an inline preview, or a viewer on the kiosk) and the customer
 * ticks the acknowledgement and continues. The sentence they accept is emitted
 * verbatim (`acknowledgement`) so the audit record carries their words.
 *
 * Plain-language doctrine — no backend names, no field codes.
 */

import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { SignatureCaptureResult } from '@retail-ops/shared-types';

export interface DisclosureReviewCaptureProps {
  /** Customer's display name — shown in the acknowledgement copy. */
  signerName: string;
  /** Disclosure the customer is acknowledging. */
  documentTitle: string;
  /** The rendered document (preview / viewer). Optional — the kiosk shows its own. */
  children?: ReactNode;
  /**
   * When true, Continue stays disabled until the host reports the document was
   * opened (`documentOpened`) — used when the document is behind a button
   * rather than rendered inline.
   */
  requireOpen?: boolean;
  documentOpened?: boolean;
  /** Called when the customer clicks Continue with the box ticked. */
  onCapture: (result: SignatureCaptureResult) => void;
  onCancel: () => void;
  /** Parent is mid-submit — controls render disabled. */
  disabled?: boolean;
}

// ── Styles (PCB brand; mirrors ClickToSignCapture) ──────────────────────────

const cardS: CSSProperties = {
  background: '#fff',
  color: '#0f172a',
  borderRadius: 10,
  maxWidth: 860,
  width: '100%',
  padding: 24,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};
const headingS: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 600, color: '#1a406a' };
const subHeadS: CSSProperties = { margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 };
const docWrapS: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#f8fafc',
  overflow: 'hidden',
  minHeight: 120,
};
const checkboxRowS: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};
const checkS: CSSProperties = { marginTop: 2, width: 18, height: 18, flex: '0 0 auto', accentColor: '#386732' };
const checkLabelS: CSSProperties = { fontSize: 13, fontWeight: 500, color: '#1f2937', lineHeight: 1.4 };
const footerS: CSSProperties = { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 };
const primaryBtnS: CSSProperties = {
  padding: '10px 18px', borderRadius: 6, border: '1px solid #386732', background: '#386732',
  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const disabledBtnS: CSSProperties = { ...primaryBtnS, background: '#9ca3af', border: '1px solid #9ca3af', cursor: 'not-allowed' };
const ghostBtnS: CSSProperties = {
  padding: '10px 18px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff',
  color: '#1f2937', fontSize: 14, fontWeight: 500, cursor: 'pointer',
};

/** The acknowledgement sentence — one definition, emitted verbatim. */
export function disclosureReviewAcknowledgement(signerName: string, documentTitle: string): string {
  return `I, ${signerName}, confirm that ${documentTitle} was presented to me and I have received and reviewed it.`;
}

export function DisclosureReviewCapture(props: DisclosureReviewCaptureProps): JSX.Element {
  const { signerName, documentTitle, children, requireOpen, documentOpened, onCapture, onCancel, disabled } = props;

  // Intent = the moment the disclosure was first shown to the customer.
  const intentTimestampRef = useRef<string>(new Date().toISOString());
  const [confirmed, setConfirmed] = useState(false);

  const acknowledgement = useMemo(
    () => disclosureReviewAcknowledgement(signerName, documentTitle),
    [signerName, documentTitle],
  );

  const openSatisfied = !requireOpen || documentOpened === true;
  const isValid = confirmed && openSatisfied;

  function handleContinue(): void {
    if (!isValid || disabled) return;
    const now = new Date().toISOString();
    onCapture({
      mode: 'review',
      acknowledgement,
      attestationTimestamp: now,
      intentTimestamp: intentTimestampRef.current,
      signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
      signerUserAgent: navigator.userAgent,
    });
  }

  return (
    <div role="dialog" aria-label="Review this disclosure" style={cardS}>
      <h2 style={headingS}>Please review: {documentTitle}</h2>
      <p style={subHeadS}>
        This disclosure is provided for your records. No signature is needed — read it, then
        tick the box and continue.
      </p>

      {children && <div style={docWrapS}>{children}</div>}

      {requireOpen && !documentOpened && (
        <p style={{ ...subHeadS, color: '#b10505' }}>Open the disclosure above before continuing.</p>
      )}

      <div style={checkboxRowS}>
        <input
          type="checkbox"
          id="drc-confirm"
          style={checkS}
          checked={confirmed}
          disabled={disabled}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <label htmlFor="drc-confirm" style={checkLabelS}>{acknowledgement}</label>
      </div>

      <div style={footerS}>
        <button type="button" style={ghostBtnS} onClick={onCancel} disabled={disabled}>
          Cancel
        </button>
        <button
          type="button"
          style={isValid && !disabled ? primaryBtnS : disabledBtnS}
          disabled={!isValid || disabled}
          onClick={handleContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

export default DisclosureReviewCapture;
